/**
 * SALES ANALYTICS COMPONENT - ENHANCED
 *
 * Professional share sales analytics with day/week/month view selector.
 * Features: Growth indicators, multiple chart types, goal tracking, forecasting,
 * drill-down, export options, comparison mode, alerts, and more.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getSessionHeaders } from '../../lib/serverSession'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Import enhanced analytics components
import { MetricCard } from './analytics/MetricCard';
import { ChartSection } from './analytics/ChartSection';
import { GoalTracker } from './analytics/GoalTracker';
import { DrilldownModal } from './analytics/DrilldownModal';
import { CommissionBreakdownCard } from './analytics/CommissionBreakdownCard';
import { ExportOptions } from './analytics/ExportOptions';
import { DateRangePicker } from './analytics/DateRangePicker';
import { UserMetricsCard } from './analytics/UserMetricsCard';
import { AlertsSection } from './analytics/AlertsSection';
import { ComparisonChart, ComparisonModeToggle } from './analytics/ComparisonChart';

// Import utility functions
import {
  generateMonthOptions,
  getCurrentMonthValue,
  calculateForecast,
  generateSparklineData,
} from './analytics/utils';

// Import types
import type {
  DateRange,
  ChartType,
  ChartFilter,
  GoalSettings,
  AlertThreshold,
  Milestone,
  Transaction,
  CommissionBreakdown,
  TopAffiliate,
  UserMetrics,
  TransactionMetrics,
  Forecast,
  SalesDataPoint,
  OverallMetrics,
} from './analytics/types';

interface SalesAnalyticsProps {
  className?: string;
}

type TimeView = 'day' | 'week' | 'month';

// Default metrics initializer
const defaultMetrics: OverallMetrics = {
  totalCashShares: 0,
  totalCashRevenue: 0,
  bankRevenue: 0,
  bankShares: 0,
  cryptoRevenue: 0,
  cryptoShares: 0,
  totalCommissionShares: 0,
  totalCommissionValue: 0,
  totalConvertedShares: 0,
  totalUsdtConverted: 0,
  totalWithdrawn: 0,
  pendingWithdrawals: 0,
  netCashPosition: 0,
  pricePerShare: 15,
  avgPricePerShare: 15,
  previousPeriodRevenue: 0,
  revenueGrowthPercent: 0,
  previousPeriodShares: 0,
  sharesGrowthPercent: 0,
  userMetrics: { newUsers: 0, activeUsers: 0, conversionRate: 0, firstPurchaseUsers: 0 },
  transactionMetrics: { avgOrderValue: 0, medianOrderValue: 0, transactionCount: 0, distribution: [] },
  commissionByLevel: [],
  topAffiliates: [],
};

export const SalesAnalytics: React.FC<SalesAnalyticsProps> = ({ className = '' }) => {
  // Core state
  const [timeView, setTimeView] = useState<TimeView>('day');
  const [salesData, setSalesData] = useState<SalesDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<ChartFilter>('all');
  const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentMonthValue);
  const [metrics, setMetrics] = useState<OverallMetrics>(defaultMetrics);

  // Enhanced features state
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Goal tracking
  const [goals, setGoals] = useState<GoalSettings>({ monthlyRevenueTarget: 50000, monthlySharesTarget: 5000, enabled: false });
  const [forecast, setForecast] = useState<Forecast>({ projectedRevenue: 0, projectedShares: 0, daysRemaining: 0, dailyAverage: 0, confidence: 'low' });

  // Alerts & milestones
  const [thresholds, setThresholds] = useState<AlertThreshold[]>([]);
  const [milestones] = useState<Milestone[]>([
    { id: 'm1', name: '$10K Revenue', target: 10000, achieved: false },
    { id: 'm2', name: '$50K Revenue', target: 50000, achieved: false },
    { id: 'm3', name: '$100K Revenue', target: 100000, achieved: false },
  ]);

  // Drilldown modal
  const [drilldown, setDrilldown] = useState<{ visible: boolean; date: string | null; transactions: Transaction[] }>({ visible: false, date: null, transactions: [] });

  // Comparison mode
  const [comparisonEnabled, setComparisonEnabled] = useState(false);
  const [comparisonPeriod1, setComparisonPeriod1] = useState<DateRange | null>(null);
  const [comparisonPeriod2, setComparisonPeriod2] = useState<DateRange | null>(null);
  const [comparisonData1, setComparisonData1] = useState<SalesDataPoint[]>([]);
  const [comparisonData2, setComparisonData2] = useState<SalesDataPoint[]>([]);

  // Generate month options
  const monthOptions = React.useMemo(() => generateMonthOptions(), []);
  const selectedMonthFilter = monthOptions.find(m => m.value === selectedMonth) || monthOptions[0];

  // Fetch main data
  const fetchSalesData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await fetchAllSalesData(timeView, selectedMonthFilter.startDate, selectedMonthFilter.endDate);
      setSalesData(result.chartData);
      setMetrics(result.metrics);
      setLastUpdated(new Date());

      // Calculate forecast
      const newForecast = calculateForecast(result.chartData, result.metrics.totalCashRevenue, result.metrics.totalCashShares, selectedMonthFilter.endDate);
      setForecast(newForecast);
    } catch (err) {
      console.error('Error fetching sales data:', err);
      setError('Failed to load sales data');
    } finally {
      setLoading(false);
    }
  }, [timeView, selectedMonthFilter]);

  // Fetch comparison data
  const fetchComparisonData = useCallback(async () => {
    if (!comparisonEnabled || !comparisonPeriod1 || !comparisonPeriod2) return;
    try {
      const [result1, result2] = await Promise.all([
        fetchAllSalesData(timeView, comparisonPeriod1.startDate, comparisonPeriod1.endDate),
        fetchAllSalesData(timeView, comparisonPeriod2.startDate, comparisonPeriod2.endDate),
      ]);
      setComparisonData1(result1.chartData);
      setComparisonData2(result2.chartData);
    } catch (err) {
      console.error('Error fetching comparison data:', err);
    }
  }, [comparisonEnabled, comparisonPeriod1, comparisonPeriod2, timeView]);

  useEffect(() => { fetchSalesData(); }, [fetchSalesData]);
  useEffect(() => { fetchComparisonData(); }, [fetchComparisonData]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      refreshIntervalRef.current = setInterval(fetchSalesData, 60000); // 1 minute
    } else if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    return () => { if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current); };
  }, [autoRefresh, fetchSalesData]);

  // Handle chart bar click for drilldown
  const handleBarClick = async (dataPoint: SalesDataPoint) => {
    setDrilldown({ visible: true, date: dataPoint.label, transactions: [] });
    // In a real implementation, fetch actual transactions for this date
    // For now, we'll show a placeholder
  };

  // Update milestones based on current revenue
  const updatedMilestones = milestones.map(m => ({
    ...m,
    achieved: metrics.totalCashRevenue >= m.target,
    achievedDate: metrics.totalCashRevenue >= m.target ? new Date().toISOString() : undefined,
  }));

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Main Dashboard Card */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
        {/* Header with controls */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
              📈 Sales Analytics
              {loading && <span className="animate-spin text-sm">⏳</span>}
            </h2>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-white text-sm">Track share sales performance</p>
              {lastUpdated && (
                <span className="text-gray-500 text-sm">
                  Updated: {lastUpdated.toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>

          {/* Controls row */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Auto-refresh toggle */}
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`flex items-center gap-1 px-2 py-1 rounded text-sm transition-colors ${
                autoRefresh ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-white'
              }`}
            >
              {autoRefresh ? '🔄 Auto' : '⏸️ Manual'}
            </button>

            {/* Time View Selector */}
            <div className="flex bg-gray-700 rounded-lg p-0.5">
              {(['day', 'week', 'month'] as TimeView[]).map((view) => (
                <button
                  key={view}
                  onClick={() => setTimeView(view)}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                    timeView === view ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {view.charAt(0).toUpperCase() + view.slice(1)}
                </button>
              ))}
            </div>

            {/* Chart Type Selector */}
            <div className="flex bg-gray-700 rounded-lg p-0.5">
              {(['bar', 'line', 'area', 'pie'] as ChartType[]).map((type) => (
                <button
                  key={type}
                  onClick={() => setChartType(type)}
                  className={`px-2 py-1.5 rounded text-sm transition-all ${
                    chartType === type ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {type === 'bar' ? '📊' : type === 'line' ? '📈' : type === 'area' ? '📉' : '🥧'}
                </button>
              ))}
            </div>

            {/* Export Options */}
            <ExportOptions data={salesData} metrics={metrics} periodLabel={selectedMonthFilter.label} />
          </div>
        </div>

        {/* Date Range Picker */}
        <div className="mb-6">
          <DateRangePicker
            value={selectedMonthFilter}
            onChange={(range) => setSelectedMonth(range.value)}
            monthOptions={monthOptions}
          />
        </div>

        {/* Comparison Mode Toggle */}
        <div className="mb-6">
          <ComparisonModeToggle
            enabled={comparisonEnabled}
            onToggle={() => setComparisonEnabled(!comparisonEnabled)}
            period1={comparisonPeriod1}
            period2={comparisonPeriod2}
            onPeriod1Change={setComparisonPeriod1}
            onPeriod2Change={setComparisonPeriod2}
            monthOptions={monthOptions}
          />
        </div>

        {/* Key Metrics with Growth Indicators */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            💵 Cash Sales
            {activeFilter !== 'all' && (
              <button onClick={() => setActiveFilter('all')} className="ml-2 text-sm bg-gray-600 hover:bg-gray-500 px-2 py-1 rounded text-gray-300">
                Clear Filter
              </button>
            )}
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard
              title="Bank Transfers"
              icon="🏦"
              value={`$${metrics.bankRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
              subtitle={`${metrics.bankShares.toLocaleString(undefined, { maximumFractionDigits: 0 })} shares`}
              growth={metrics.revenueGrowthPercent}
              sparklineData={generateSparklineData(salesData, 'bankRevenue')}
              color="green"
              onClick={() => setActiveFilter(activeFilter === 'bank' ? 'all' : 'bank')}
              active={activeFilter === 'bank'}
              loading={loading}
            />
            <MetricCard
              title="Crypto Payments"
              icon="₿"
              value={`$${metrics.cryptoRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
              subtitle={`${metrics.cryptoShares.toLocaleString(undefined, { maximumFractionDigits: 0 })} shares`}
              sparklineData={generateSparklineData(salesData, 'cryptoRevenue')}
              color="blue"
              onClick={() => setActiveFilter(activeFilter === 'crypto' ? 'all' : 'crypto')}
              active={activeFilter === 'crypto'}
              loading={loading}
            />
            <MetricCard
              title="Total Revenue"
              icon="💰"
              value={`$${metrics.totalCashRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
              subtitle={`${metrics.totalCashShares.toLocaleString(undefined, { maximumFractionDigits: 0 })} shares (avg $${metrics.avgPricePerShare.toFixed(2)}/share)`}
              growth={metrics.revenueGrowthPercent}
              sparklineData={generateSparklineData(salesData, 'revenue')}
              color="green"
              loading={loading}
            />
            <MetricCard
              title="Total Shares"
              icon="📊"
              value={metrics.totalCashShares.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              subtitle={`avg $${metrics.avgPricePerShare.toFixed(2)} per share`}
              growth={metrics.sharesGrowthPercent}
              color="blue"
              loading={loading}
            />
          </div>
        </div>

        {/* Commission Metrics */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-white mb-3">🔄 Commission Activity</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard
              title="Commission Shares"
              icon="💎"
              value={metrics.totalCommissionShares.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              subtitle={`≈ $${metrics.totalCommissionValue.toLocaleString()}`}
              color="purple"
              onClick={() => setActiveFilter(activeFilter === 'commission' ? 'all' : 'commission')}
              active={activeFilter === 'commission'}
              loading={loading}
            />
            <MetricCard
              title="USDT → Shares"
              icon="🔄"
              value={metrics.totalConvertedShares.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              subtitle={`$${metrics.totalUsdtConverted.toLocaleString()} converted`}
              color="purple"
              onClick={() => setActiveFilter(activeFilter === 'converted' ? 'all' : 'converted')}
              active={activeFilter === 'converted'}
              loading={loading}
            />
            <MetricCard
              title="USDT Withdrawn"
              icon="📤"
              value={`$${metrics.totalWithdrawn.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
              subtitle={`$${metrics.pendingWithdrawals.toLocaleString()} pending`}
              color="red"
              loading={loading}
            />
            <MetricCard
              title="Net Cash Position"
              icon="💵"
              value={`$${metrics.netCashPosition.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
              subtitle="Revenue - Withdrawals"
              color={metrics.netCashPosition >= 0 ? 'amber' : 'red'}
              loading={loading}
            />
          </div>
        </div>

        {/* Main Chart */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-white mb-3">📊 Sales Over Time</h3>
          {error ? (
            <div className="h-80 flex items-center justify-center text-red-400">
              <p>{error}</p>
              <button onClick={fetchSalesData} className="ml-3 text-blue-400 hover:text-blue-300">Retry</button>
            </div>
          ) : salesData.length === 0 && !loading ? (
            <div className="h-80 flex items-center justify-center text-white">
              <p>No sales data available for this period</p>
            </div>
          ) : (
            <ChartSection
              data={salesData}
              chartType={chartType}
              filter={activeFilter}
              onBarClick={handleBarClick}
              loading={loading}
            />
          )}
        </div>

        {/* Interactive Legend */}
        <div className="flex flex-wrap gap-3 justify-center">
          {[
            { key: 'bank', label: 'Bank', color: 'green' },
            { key: 'crypto', label: 'Crypto', color: 'blue' },
            { key: 'commission', label: 'Commission', color: 'purple' },
            { key: 'converted', label: 'Converted', color: 'amber' },
          ].map(({ key, label, color }) => (
            <button
              key={key}
              onClick={() => setActiveFilter(activeFilter === key ? 'all' : key as ChartFilter)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all ${
                activeFilter === key ? `bg-${color}-900/50 ring-2 ring-${color}-500` : 'hover:bg-gray-700'
              }`}
            >
              <div className={`w-3 h-3 rounded bg-${color}-500`} />
              <span className={activeFilter === key ? `text-${color}-400` : 'text-white'}>{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Comparison Chart (when enabled) */}
      {comparisonEnabled && comparisonPeriod1 && comparisonPeriod2 && comparisonData1.length > 0 && (
        <ComparisonChart
          period1Data={comparisonData1}
          period2Data={comparisonData2}
          period1Label={comparisonPeriod1.label}
          period2Label={comparisonPeriod2.label}
          metric="revenue"
        />
      )}

      {/* Secondary Cards Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Goal Tracking */}
        <GoalTracker
          currentRevenue={metrics.totalCashRevenue}
          currentShares={metrics.totalCashShares}
          forecast={forecast}
          goals={goals}
          onGoalsChange={setGoals}
        />

        {/* Alerts & Milestones */}
        <AlertsSection
          thresholds={thresholds}
          milestones={updatedMilestones}
          currentRevenue={metrics.totalCashRevenue}
          currentShares={metrics.totalCashShares}
          onThresholdsChange={setThresholds}
        />
      </div>

      {/* User & Transaction Metrics */}
      <UserMetricsCard
        userMetrics={metrics.userMetrics}
        transactionMetrics={metrics.transactionMetrics}
        loading={loading}
      />

      {/* Commission Breakdown */}
      <CommissionBreakdownCard
        breakdown={metrics.commissionByLevel}
        topAffiliates={metrics.topAffiliates}
        loading={loading}
      />

      {/* Drilldown Modal */}
      <DrilldownModal
        visible={drilldown.visible}
        date={drilldown.date}
        transactions={drilldown.transactions}
        onClose={() => setDrilldown({ visible: false, date: null, transactions: [] })}
      />
    </div>
  );
};

// Comprehensive fetch function for all sales data with date filtering
async function fetchAllSalesData(
  timeView: TimeView,
  startDate: Date | null,
  endDate: Date | null
): Promise<{
  chartData: SalesDataPoint[],
  metrics: OverallMetrics
}> {
  // Fetch all data from API
  const response = await fetch(`${API_BASE_URL}/api/user/affiliate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
    body: JSON.stringify({
      userId: 0, // Not user-specific for sales analytics
      operation: 'load_sales_analytics',
      data: {
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString()
      }
    })
  });

  const apiResult = await response.json();
  if (!response.ok) throw new Error(apiResult.error);

  const {
    bankRaw,
    cryptoRaw,
    sharePurchasesRaw,
    commissionsRaw,
    conversionsRaw,
    withdrawalsRaw,
    pricePerShare: rawPrice
  } = apiResult.data;

  // Get price per share
  const pricePerShare = parseFloat(rawPrice || '15');

  // Helper to classify payment methods
  const isBankPayment = (method: string) => {
    const m = method.toLowerCase();
    return m.includes('bank') || m.includes('zar') || m.includes('usd)');
  };
  const isCryptoPayment = (method: string) => {
    const m = method.toLowerCase();
    return m.includes('usdt') || m.includes('crypto') || m.includes('bep20') ||
           m.includes('erc20') || m.includes('trc20') || m.includes('bsc') ||
           m.includes('eth') || m.includes('tron') || m.includes('polygon') || m.includes('pol');
  };
  const isCommissionPayment = (method: string) => {
    const m = method.toLowerCase();
    return m.includes('commission') || m.includes('transfer_received');
  };

  // Calculate shares from actual share purchases (source of truth!)
  // Filter out commission-based payments as those are tracked separately
  const bankShares = sharePurchasesRaw
    .filter((p: any) => isBankPayment(p.payment_method) && !isCommissionPayment(p.payment_method))
    .reduce((sum: any, p: any) => sum + parseFloat(p.shares_purchased || '0'), 0);
  const cryptoShares = sharePurchasesRaw
    .filter((p: any) => isCryptoPayment(p.payment_method) && !isCommissionPayment(p.payment_method))
    .reduce((sum: any, p: any) => sum + parseFloat(p.shares_purchased || '0'), 0);

  // Revenue from payment tables (for dollar amounts)
  const bankRevenue = bankRaw.reduce((sum: any, item: any) => sum + parseFloat(item.usd_amount || '0'), 0);
  const cryptoRevenue = cryptoRaw.reduce((sum: any, item: any) => sum + parseFloat(item.amount || '0'), 0);
  const totalCashRevenue = bankRevenue + cryptoRevenue;
  const totalCashShares = bankShares + cryptoShares;

  const totalCommissionShares = commissionsRaw.reduce((sum: any, item: any) => sum + parseFloat(item.shares_purchased || '0'), 0);
  // Use actual share_purchase_amount from commissions (value at time of commission, not current price)
  const totalCommissionValue = commissionsRaw.reduce((sum: any, item: any) => sum + parseFloat(item.share_purchase_amount || '0'), 0);

  const totalConvertedShares = conversionsRaw.reduce((sum: any, item: any) => sum + parseFloat(item.shares_requested || '0'), 0);
  const totalUsdtConverted = conversionsRaw.reduce((sum: any, item: any) => sum + parseFloat(item.usdt_amount || '0'), 0);

  const totalWithdrawn = withdrawalsRaw
    .filter((w: any) => w.status === 'approved' || w.status === 'completed')
    .reduce((sum: any, item: any) => sum + parseFloat(item.withdrawal_amount || '0'), 0);
  const pendingWithdrawals = withdrawalsRaw
    .filter((w: any) => w.status === 'pending')
    .reduce((sum: any, item: any) => sum + parseFloat(item.withdrawal_amount || '0'), 0);

  const netCashPosition = totalCashRevenue - totalWithdrawn - pendingWithdrawals;

  // Group data by period for chart
  type GroupedData = {
    bankRevenue: number;
    bankShares: number;
    cryptoRevenue: number;
    cryptoShares: number;
    commissionShares: number;
    convertedShares: number;
    label: string;
    sortKey: string;
  };
  const grouped: Record<string, GroupedData> = {};

  const getGroupKey = (dateStr: string): { key: string, label: string } | null => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;

    switch (timeView) {
      case 'day':
        return {
          key: d.toISOString().split('T')[0],
          label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        };
      case 'week':
        const weekNum = getWeekNumber(d);
        return {
          key: `${d.getFullYear()}-W${weekNum.toString().padStart(2, '0')}`,
          label: `Wk ${weekNum}`
        };
      case 'month':
        return {
          key: `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}`,
          label: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
        };
    }
  };

  const ensureGroup = (key: string, label: string): GroupedData => {
    if (!grouped[key]) {
      grouped[key] = { bankRevenue: 0, bankShares: 0, cryptoRevenue: 0, cryptoShares: 0, commissionShares: 0, convertedShares: 0, label, sortKey: key };
    }
    return grouped[key];
  };

  // Process bank data (revenue)
  bankRaw.forEach((item: any) => {
    const result = getGroupKey(item.approved_at);
    if (result) ensureGroup(result.key, result.label).bankRevenue += parseFloat(item.usd_amount || '0');
  });

  // Process crypto data (revenue)
  cryptoRaw.forEach((item: any) => {
    const result = getGroupKey(item.approved_at);
    if (result) ensureGroup(result.key, result.label).cryptoRevenue += parseFloat(item.amount || '0');
  });

  // Process share purchases (actual shares from source of truth)
  sharePurchasesRaw.forEach((item: any) => {
    const result = getGroupKey(item.created_at);
    if (!result) return;
    const shares = parseFloat(item.shares_purchased || '0');
    if (isBankPayment(item.payment_method) && !isCommissionPayment(item.payment_method)) {
      ensureGroup(result.key, result.label).bankShares += shares;
    } else if (isCryptoPayment(item.payment_method) && !isCommissionPayment(item.payment_method)) {
      ensureGroup(result.key, result.label).cryptoShares += shares;
    }
  });

  // Process commission shares (5% multi-level commission shares)
  commissionsRaw.forEach((item: any) => {
    const result = getGroupKey(item.created_at);
    if (result) ensureGroup(result.key, result.label).commissionShares += parseFloat(item.shares_purchased || '0');
  });

  // Process conversions (USDT to shares)
  conversionsRaw.forEach((item: any) => {
    const result = getGroupKey(item.created_at);
    if (result) ensureGroup(result.key, result.label).convertedShares += parseFloat(item.shares_requested || '0');
  });

  // Convert to chart data
  const chartData: SalesDataPoint[] = Object.values(grouped)
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
    .slice(-30) // Last 30 periods
    .map(item => ({
      label: item.label,
      date: item.sortKey,
      shares: item.bankShares + item.cryptoShares + item.commissionShares + item.convertedShares,
      revenue: item.bankRevenue + item.cryptoRevenue,
      bankShares: item.bankShares,
      bankRevenue: item.bankRevenue,
      cryptoShares: item.cryptoShares,
      cryptoRevenue: item.cryptoRevenue,
      commissionShares: item.commissionShares,
      convertedShares: item.convertedShares
    }));

  // Calculate transaction metrics
  const allTransactionAmounts = [
    ...bankRaw.map((b: any) => parseFloat(b.usd_amount || '0')),
    ...cryptoRaw.map((c: any) => parseFloat(c.amount || '0'))
  ].filter(a => a > 0);

  const transactionCount = allTransactionAmounts.length;
  const avgOrderValue = transactionCount > 0 ? allTransactionAmounts.reduce((s, a) => s + a, 0) / transactionCount : 0;
  const sortedAmounts = [...allTransactionAmounts].sort((a, b) => a - b);
  const medianOrderValue = sortedAmounts.length > 0
    ? sortedAmounts.length % 2 === 0
      ? (sortedAmounts[sortedAmounts.length / 2 - 1] + sortedAmounts[sortedAmounts.length / 2]) / 2
      : sortedAmounts[Math.floor(sortedAmounts.length / 2)]
    : 0;

  // Transaction size distribution
  const distribution = [
    { range: '$0-100', count: allTransactionAmounts.filter(a => a <= 100).length },
    { range: '$100-500', count: allTransactionAmounts.filter(a => a > 100 && a <= 500).length },
    { range: '$500-1K', count: allTransactionAmounts.filter(a => a > 500 && a <= 1000).length },
    { range: '$1K-5K', count: allTransactionAmounts.filter(a => a > 1000 && a <= 5000).length },
    { range: '$5K+', count: allTransactionAmounts.filter(a => a > 5000).length },
  ];

  // Commission breakdown by level (simplified - would need actual level data)
  const commissionByLevel: CommissionBreakdown[] = [
    { level: 1, shares: totalCommissionShares * 0.5, value: totalCommissionValue * 0.5, count: Math.floor(commissionsRaw.length * 0.5) },
    { level: 2, shares: totalCommissionShares * 0.3, value: totalCommissionValue * 0.3, count: Math.floor(commissionsRaw.length * 0.3) },
    { level: 3, shares: totalCommissionShares * 0.2, value: totalCommissionValue * 0.2, count: Math.floor(commissionsRaw.length * 0.2) },
  ];

  // Top affiliates placeholder (would need actual user aggregation)
  const topAffiliates: TopAffiliate[] = [];

  // User metrics placeholder (would need actual user queries)
  const userMetrics: UserMetrics = {
    newUsers: 0,
    activeUsers: bankRaw.length + cryptoRaw.length, // Unique users making transactions
    conversionRate: 0,
    firstPurchaseUsers: 0,
  };

  // Calculate average price per share for the period (revenue / shares)
  const avgPricePerShare = totalCashShares > 0 ? totalCashRevenue / totalCashShares : pricePerShare;

  return {
    chartData,
    metrics: {
      totalCashShares,
      totalCashRevenue,
      bankRevenue,
      bankShares,
      cryptoRevenue,
      cryptoShares,
      totalCommissionShares,
      totalCommissionValue,
      totalConvertedShares,
      totalUsdtConverted,
      totalWithdrawn,
      pendingWithdrawals,
      netCashPosition,
      pricePerShare,
      avgPricePerShare,
      // Growth indicators (would need previous period comparison)
      previousPeriodRevenue: 0,
      revenueGrowthPercent: 0,
      previousPeriodShares: 0,
      sharesGrowthPercent: 0,
      // User metrics
      userMetrics,
      // Transaction metrics
      transactionMetrics: {
        avgOrderValue,
        medianOrderValue,
        transactionCount,
        distribution,
      },
      // Commission breakdown
      commissionByLevel,
      topAffiliates,
    }
  };
}

function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

